import type {
  Photo,
  PhotoTag,
  CreatePhotoInput,
  UpdatePhotoInput,
  CreatePhotoTagInput,
  PresignedUrlRequest,
  PresignedUrlResponse,
  PresignedDownloadUrlResponse,
} from '@peles/shared';
import { apiClient } from './client';
import axios from 'axios';

export async function getPresignedUploadUrl(
  data: PresignedUrlRequest,
): Promise<PresignedUrlResponse> {
  const { data: result } = await apiClient.post<PresignedUrlResponse>(
    '/photos/presigned-url',
    data,
  );
  return result;
}

export async function getPresignedDownloadUrl(id: string): Promise<PresignedDownloadUrlResponse> {
  const { data: result } = await apiClient.get<PresignedDownloadUrlResponse>(`/photos/${id}/url`);
  return result;
}

export async function getBatchPhotoUrls(ids: string[]): Promise<Record<string, string>> {
  const { data } = await apiClient.post<{ urls: Record<string, string> }>('/photos/batch-urls', {
    ids,
  });
  return data.urls;
}

export async function uploadFileToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type },
    onUploadProgress: (e) => {
      if (onProgress && e.total) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    },
  });
}

export async function listPhotos(filters?: {
  entityType?: string;
  entityId?: string;
  caption?: string;
  page?: number;
  limit?: number;
}): Promise<{ photos: Photo[]; total: number }> {
  const params = new URLSearchParams();
  if (filters?.entityType) params.set('entityType', filters.entityType);
  if (filters?.entityId) params.set('entityId', filters.entityId);
  if (filters?.caption) params.set('caption', filters.caption);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.limit) params.set('limit', String(filters.limit));

  const { data } = await apiClient.get<{ photos: Photo[]; total: number }>('/photos', { params });
  return data;
}

export async function getPhoto(id: string): Promise<Photo> {
  const { data } = await apiClient.get<Photo>(`/photos/${id}`);
  return data;
}

export async function createPhoto(input: CreatePhotoInput): Promise<Photo> {
  const { data } = await apiClient.post<Photo>('/photos', input);
  return data;
}

export async function updatePhoto(id: string, input: UpdatePhotoInput): Promise<Photo> {
  const { data } = await apiClient.patch<Photo>(`/photos/${id}`, input);
  return data;
}

export async function deletePhoto(id: string): Promise<void> {
  await apiClient.delete(`/photos/${id}`);
}

export async function addPhotoTag(photoId: string, tag: CreatePhotoTagInput): Promise<PhotoTag> {
  const { data } = await apiClient.post<PhotoTag>(`/photos/${photoId}/tags`, tag);
  return data;
}

export async function removePhotoTag(photoId: string, tagId: string): Promise<void> {
  await apiClient.delete(`/photos/${photoId}/tags/${tagId}`);
}
